import React, { useState, useEffect } from "react";
import {
  entities,
  getDisplayMessage,
  uploadFile,
  parseUploadBlobIdFromFileUrl,
} from "@/api/apiClient";
import { useMutation } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ArrowLeft,
  Download,
  Printer,
  Edit2,
  Save,
  X,
  AlertCircle,
  PenLine,
  CopyPlus,
  Mail,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { useAuth } from "@/lib/AuthContext";
import { userHasModuleAccess } from "@/lib/moduleAccess";
import { format } from "date-fns";
import { parseDataAbertura } from "@/lib/formatarDataAbertura";
import { ptBR } from "date-fns/locale";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import {
  exportPropostaVisualizarPdf,
  buildPropostaVisualizarPdfBlob,
} from "@/lib/propostaVisualizarPdfExport";
import {
  removerReferenciasEdital,
  formatReferentePregao,
} from "@/utils/removerReferenciasEdital";
import { getPreposicaoInstituicaoDisplay } from "@/utils/preposicaoInstituicao";
import {
  TIPO_LINHA,
  getTipoLinha,
  aplicarColunasCalculadas,
  somarColunaAteSubtotal,
} from "@/utils/propostaItensCalculations";
import AuthenticatedUploadImage from "@/components/AuthenticatedUploadImage";
import { RichHtmlFragment } from "@/components/rich-text/RichHtmlFragment.jsx";
import {
  displayField,
  prosePdfClass,
  propostaCardClass,
  propostaTituloSecaoClass,
  propostaBlocoGapClass,
  propostaMaxReadableClass,
  getPropostaColunaTabelaClass,
  getPropostaColunasWidthsPercent,
  getPropostaColElementStyle,
  getPropostaTabelaItensMinWidthPx,
  getSubtotalLabelColSpan,
  getPropostaThHeaderClass,
  isPropostaColunaDescricao,
} from "@/utils/propostaDisplay";
import BlocoAssinaturaEmpresa from "@/components/empresa/BlocoAssinaturaEmpresa";

/** YYYY-MM-DD no fuso local (evita dia errado vs. toISOString() em UTC). */
function getDataLocalHoje() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function toDateInputValue(value) {
  if (!value) return "";
  if (typeof value === "string") {
    return value.includes("T") ? value.split("T")[0] : value;
  }
  const d = new Date(value);
  return isNaN(d.getTime()) ? "" : d.toISOString().split("T")[0];
}

export default function VisualizarPropostaPage() {
  const navigate = useNavigate();
  const { user, empresa } = useAuth();
  const podeUsarAssina = userHasModuleAccess(user, empresa, "Assina");
  const urlParams = new URLSearchParams(window.location.search);
  const propostaId = urlParams.get('id');

  const [modoEdicao, setModoEdicao] = useState(false);
  const [localProposta, setLocalProposta] = useState("");
  const [dataProposta, setDataProposta] = useState("");
  const [valorTotal, setValorTotal] = useState(0);
  const [textoValorTotal, setTextoValorTotal] = useState("Valor Global da Proposta de Preços R$");

  const [proposta, setProposta] = useState(null);
  const [edital, setEdital] = useState(null);
  const [config, setConfig] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [geratingPDF, setGeratingPDF] = useState(false);
  const [assinaBusy, setAssinaBusy] = useState(false);
  const [versionando, setVersionando] = useState(false);
  const [enviandoEmail, setEnviandoEmail] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  // Atualiza o título da página para ser usado no nome do PDF
  useEffect(() => {
    if (edital) {
      const tituloOuInstituicao = edital.titulo_licitacao || edital.instituicao || edital.nome_arquivo;
      if (tituloOuInstituicao) {
        const tituloLimpo = tituloOuInstituicao
          .replace(/[<>:"/\\|?*]/g, '')
          .replace(/\s+/g, ' ')
          .trim();
        document.title = `Proposta de Preço - ${tituloLimpo}`;
      } else {
        document.title = "Proposta de Preço";
      }
    } else {
      document.title = "Proposta de Preço";
    }
    
    return () => {
      document.title = "Licitações";
    };
  }, [edital]);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Verifica se o ID foi fornecido
      if (!propostaId) {
        setError("ID da proposta não foi fornecido na URL.");
        setIsLoading(false);
        setTimeout(() => {
          navigate(createPageUrl("Propostas"));
        }, 3000);
        return;
      }

      // Fetch Config first as it's broadly used
      const configs = await entities.ConfiguracaoEmpresa.list();
      const fetchedConfig = configs[0] || null;
      setConfig(
        fetchedConfig
          ? {
              ...fetchedConfig,
              razao_social:
                fetchedConfig.razao_social ??
                fetchedConfig.razaoSocial ??
                "",
              cnpj: fetchedConfig.cnpj ?? fetchedConfig.CNPJ ?? "",
              cabecalho_url:
                fetchedConfig.cabecalho_url ?? fetchedConfig.cabecalhoUrl ?? null,
              rodape_url:
                fetchedConfig.rodape_url ?? fetchedConfig.rodapeUrl ?? null,
              assinatura_url:
                fetchedConfig.assinatura_url ?? fetchedConfig.assinaturaUrl ?? null,
              marca_dagua_url:
                fetchedConfig.marca_dagua_url ?? fetchedConfig.marcaDaguaUrl ?? null,
            }
          : null
      );

      // Busca a proposta usando getById (mais eficiente)
      let currentProposta;
      try {
        currentProposta = await entities.Proposta.get(propostaId);
      } catch (error) {
        console.warn("Erro ao buscar proposta por ID, tentando list:", error);
        // Fallback para list + find se getById não funcionar
        const propostas = await entities.Proposta.list();
        currentProposta = propostas.find(p => p.id === propostaId);
      }

      if (!currentProposta) {
        setError(`Proposta com ID "${propostaId}" não foi encontrada.`);
        setIsLoading(false);
        setTimeout(() => {
          navigate(createPageUrl("Propostas"));
        }, 3000);
        return;
      }

      // Normaliza campos: aceita tanto snake_case quanto camelCase
      const propostaNormalizada = {
        ...currentProposta,
        ao_instituicao: currentProposta.ao_instituicao ?? currentProposta.aoInstituicao ?? null,
        agente_contratacao: currentProposta.agente_contratacao ?? currentProposta.agenteContratacao ?? null,
        pregao_numero: currentProposta.pregao_numero ?? currentProposta.pregaoNumero ?? null,
        processo_numero: currentProposta.processo_numero ?? currentProposta.processoNumero ?? null,
        sistema: currentProposta.sistema ?? null,
        identificacao: currentProposta.identificacao ?? null,
        edital_id: currentProposta.edital_id ?? currentProposta.editalId ?? null,
        prazo_validade: currentProposta.prazo_validade ?? currentProposta.prazoValidade ?? null,
        prazo_entrega: currentProposta.prazo_entrega ?? currentProposta.prazoEntrega ?? null,
        prazo_vigencia: currentProposta.prazo_vigencia ?? currentProposta.prazoVigencia ?? null,
        prazo_pagamento: currentProposta.prazo_pagamento ?? currentProposta.prazoPagamento ?? null,
        local_data_proposta: currentProposta.local_data_proposta ?? currentProposta.localDataProposta ?? null,
        data_proposta: currentProposta.data_proposta ?? currentProposta.dataProposta ?? null,
        valor_total_proposta: currentProposta.valor_total_proposta ?? currentProposta.valorTotalProposta ?? null,
        texto_valor_total: currentProposta.texto_valor_total ?? currentProposta.textoValorTotal ?? null,
        numero_proposta: currentProposta.numero_proposta ?? currentProposta.numeroProposta ?? null,
        colunas_itens: currentProposta.colunas_itens ?? currentProposta.colunasItens ?? null,
        campos_extras: currentProposta.campos_extras ?? currentProposta.camposExtras ?? null,
        prazos_extras: (() => {
          const raw =
            currentProposta.prazos_extras ?? currentProposta.prazosExtras;
          return Array.isArray(raw) ? raw : [];
        })(),
      };

      setProposta(propostaNormalizada);

      // Fetch Edital if a valid edital_id exists in the fetched proposal
      const editalId = propostaNormalizada.edital_id || propostaNormalizada.editalId;
      if (editalId) {
        try {
          const currentEdital = await entities.Edital.get(editalId);
          setEdital(currentEdital);
        } catch (error) {
          console.warn("Erro ao buscar edital por ID, tentando list:", error);
          const editais = await entities.Edital.list();
          const currentEdital = editais.find(e => e.id === editalId);
          setEdital(currentEdital || null);
        }
      } else {
        setEdital(null);
      }

    } catch (error) {
      console.error("Failed to load data:", error);
      setError(getDisplayMessage(error, 'Erro ao carregar dados.'));
      setTimeout(() => {
        navigate(createPageUrl("Propostas"));
      }, 3000);
    } finally {
      setIsLoading(false);
    }
  };

  const updateMutation = useMutation({
    mutationFn: async (data) => {
      return entities.Proposta.update(propostaId, data);
    },
    onSuccess: (updatedProposta) => {
      setProposta(prev => ({ ...prev, ...updatedProposta }));
      setModoEdicao(false);
    },
    onError: (error) => {
      console.error("Failed to update proposal:", error);
      setError(getDisplayMessage(error, 'Erro ao atualizar proposta.'));
    }
  });

  useEffect(() => {
    if (proposta && config) {
      setLocalProposta(proposta.local_data_proposta || `${config?.municipio || 'Recife'}/${config?.uf || 'PE'}`);
      setDataProposta(toDateInputValue(proposta.data_proposta) || getDataLocalHoje());
      setValorTotal(proposta.valor_total_proposta || 0);
      setTextoValorTotal(proposta.texto_valor_total || "Valor Global da Proposta de Preços R$");
    }
  }, [proposta, config]);

  const handleSalvarEdicao = () => {
    updateMutation.mutate({
      local_data_proposta: localProposta,
      data_proposta: dataProposta,
      valor_total_proposta: valorTotal,
      texto_valor_total: textoValorTotal
    });
  };

  const runExportPdf = async (openPrintDialog) => {
    setGeratingPDF(true);
    toast.info("Gerando PDF (texto pesquisável)...");
    try {
      const dataExtenso = formatDate(dataProposta) || dataProposta || "";
      const result = await exportPropostaVisualizarPdf({
        edital,
        proposta,
        config,
        localProposta,
        dataPropostaExtenso: dataExtenso,
        openPrintDialog,
      });
      toast.success("PDF gerado com texto selecionável.");
      if (!result.withinLimit) {
        toast.warning(
          `O PDF ficou com cerca de ${(result.sizeBytes / (1024 * 1024)).toFixed(1)} MB (limite desejado: 10 MB).`
        );
      }
    } catch (error) {
      console.error("Erro ao gerar PDF:", error);
      toast.error(getDisplayMessage(error, "Falha ao gerar PDF."));
    } finally {
      setGeratingPDF(false);
    }
  };

  const handlePrint = async () => {
    await runExportPdf(true);
  };

  const handleDownloadPDF = async () => {
    await runExportPdf(false);
  };

  const handleAssina = async () => {
    if (!proposta || !config) return;
    if (!podeUsarAssina) {
      toast.error("Seu usuário não tem permissão para o módulo Assina.");
      return;
    }
    setAssinaBusy(true);
    toast.info("Gerando PDF e preparando envelope de assinatura…");
    try {
      const dataExtenso = formatDate(dataProposta) || dataProposta || "";
      const { blob, fileName, withinLimit } = await buildPropostaVisualizarPdfBlob({
        edital,
        proposta,
        config,
        localProposta,
        dataPropostaExtenso: dataExtenso,
      });
      if (!withinLimit) {
        toast.warning(
          `O PDF ficou grande (~${(blob.size / (1024 * 1024)).toFixed(1)} MB). O envio ao Assina pode demorar.`
        );
      }

      const tituloEdital =
        edital?.titulo_licitacao || edital?.tituloLicitacao || edital?.instituicao || edital?.nome_arquivo || "";
      const numProp = proposta.numero_proposta || `#${String(proposta.id).slice(0, 8)}`;
      const tituloEnvelope = tituloEdital
        ? `Proposta ${numProp} — ${String(tituloEdital).slice(0, 200)}`
        : `Proposta de preço ${numProp}`;

      const envelope = await entities.Assina.create({ titulo: tituloEnvelope });
      const envelopeId = envelope?.id ?? envelope?.Id;
      if (!envelopeId) {
        throw new Error("Resposta do servidor sem ID do envelope.");
      }

      const file = new File([blob], fileName, { type: "application/pdf" });
      const up = await uploadFile(file);
      const blobId = parseUploadBlobIdFromFileUrl(up.file_url);
      if (!blobId) {
        throw new Error("Não foi possível obter o id do arquivo após o upload.");
      }
      await entities.Assina.addDocument(envelopeId, blobId);

      const nomeSign = (config.socio_nome ?? config.socioNome ?? "").trim();
      const emailSign = (config.socio_email ?? config.socioEmail ?? "").trim();
      const emailOk = emailSign.includes("@") && nomeSign.length >= 3;

      if (emailOk) {
        try {
          await entities.Assina.addSigners(envelopeId, [
            {
              nome: nomeSign,
              email: emailSign,
              ordem_assinatura: 0,
              nivel: "simples",
            },
          ]);
          await entities.Assina.send(envelopeId);
          toast.success(
            "Envelope criado com o PDF da proposta. Convite de assinatura enviado ao e-mail do representante legal."
          );
        } catch (sendErr) {
          console.error("Assina enviar após proposta:", sendErr);
          toast.warning(
            getDisplayMessage(
              sendErr,
              "PDF anexado ao envelope. Inclua signatários e clique em Enviar na tela Assina."
            )
          );
        }
      } else {
        toast.success(
          "PDF anexado ao envelope. Configure nome e e-mail do representante legal em Configuração da empresa para envio automático, ou adicione signatários na tela Assina."
        );
      }

      navigate(`${createPageUrl("Assina")}?envelope=${encodeURIComponent(envelopeId)}`);
    } catch (error) {
      console.error("Erro ao preparar Assina:", error);
      toast.error(getDisplayMessage(error, "Não foi possível preparar o envelope de assinatura."));
    } finally {
      setAssinaBusy(false);
    }
  };

  const handleVersionarProposta = async () => {
    if (!propostaId || versionando) return;
    const confirmar = window.confirm(
      "Criar uma nova versão editável desta proposta? A proposta atual será preservada."
    );
    if (!confirmar) return;

    setVersionando(true);
    try {
      const novaProposta = await entities.Proposta.versionar(propostaId);
      toast.success("Nova versão da proposta criada.");
      navigate(createPageUrl("CriarProposta") + `?id=${novaProposta.id}`);
    } catch (error) {
      console.error("Erro ao versionar proposta:", error);
      toast.error(getDisplayMessage(error, "Não foi possível versionar a proposta."));
    } finally {
      setVersionando(false);
    }
  };

  const handleEnviarEmail = async () => {
    if (!proposta || !config || enviandoEmail) return;
    const destinatario = window.prompt("Informe o e-mail do destinatário:");
    const email = destinatario?.trim();
    if (!email) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Informe um e-mail válido.");
      return;
    }

    setEnviandoEmail(true);
    toast.info("Gerando PDF e enviando proposta por e-mail...");
    try {
      const dataExtenso = formatDate(dataProposta) || dataProposta || "";
      const { blob, fileName, withinLimit } = await buildPropostaVisualizarPdfBlob({
        edital,
        proposta,
        config,
        localProposta,
        dataPropostaExtenso: dataExtenso,
      });
      if (!withinLimit) {
        toast.warning(
          `O PDF ficou grande (~${(blob.size / (1024 * 1024)).toFixed(1)} MB). O envio pode demorar.`
        );
      }
      const file = new File([blob], fileName, { type: "application/pdf" });
      const up = await uploadFile(file);
      const uploadBlobId = up.upload_blob_id || parseUploadBlobIdFromFileUrl(up.file_url);
      if (!uploadBlobId) {
        throw new Error("Não foi possível anexar o PDF gerado.");
      }

      const numero = proposta.numero_proposta || `#${String(proposta.id).slice(0, 8)}`;
      await entities.Proposta.enviarEmail(proposta.id, {
        destinatario: email,
        upload_blob_id: uploadBlobId,
        assunto: `Proposta de preços ${numero}`,
        mensagem: "Segue em anexo nossa proposta de preços para apreciação.",
      });
      toast.success(`Proposta enviada para ${email}.`);
    } catch (error) {
      console.error("Erro ao enviar proposta por e-mail:", error);
      toast.error(getDisplayMessage(error, "Não foi possível enviar a proposta por e-mail."));
    } finally {
      setEnviandoEmail(false);
    }
  };

  const formatCurrency = (value) => {
    if (value === undefined || value === null) return "R$ 0,00";
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(value));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      let date;
      if (dateString.includes('-') && dateString.length === 10) {
        date = new Date(dateString + 'T12:00:00');
      } else {
        date = new Date(dateString);
      }
      
      if (isNaN(date.getTime())) return '';
      return format(date, "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
    } catch {
      return dateString;
    }
  };

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-4 md:p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <Button
              variant="outline"
              size="icon"
              onClick={() => navigate(createPageUrl("Propostas"))}
              className="rounded-xl"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <h1 className="text-3xl font-bold text-slate-900">Erro ao Carregar Proposta</h1>
          </div>
          
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <div className="space-y-2">
                <p className="font-semibold">{error}</p>
                <p className="text-sm">Você será redirecionado para a lista de propostas em alguns segundos...</p>
              </div>
            </AlertDescription>
          </Alert>

          <Card className="border-none shadow-xl bg-white/90 backdrop-blur-sm">
            <CardContent className="p-12 text-center">
              <p className="text-slate-500 mb-4">A proposta solicitada não pôde ser carregada.</p>
              <Button onClick={() => navigate(createPageUrl("Propostas"))}>
                Voltar para Propostas
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Loading state
  if (isLoading || !proposta || !config) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-4 md:p-8">
        <div className="max-w-7xl mx-auto">
          <Card className="border-none shadow-xl bg-white/90 backdrop-blur-sm">
            <CardContent className="p-12 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-slate-500">Carregando proposta...</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const instituicaoFmt = getPreposicaoInstituicaoDisplay(proposta.ao_instituicao);
  const numeroPropostaDisplay = proposta.numero_proposta || `#${proposta.id.slice(0, 8)}`;
  const instituicaoDisplay = displayField(removerReferenciasEdital(instituicaoFmt.nomeExibicao));
  const dataPropostaDisplay = formatDate(dataProposta || proposta.data_proposta) || "Não informado";
  const valorTotalDisplay = formatCurrency(valorTotal || proposta.valor_total_proposta || 0);

  return (
    <>


      <div className="bg-gradient-to-br from-slate-50 to-blue-50 p-4 md:p-6 no-print">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="icon"
                onClick={() => navigate(createPageUrl("Propostas"))}
                className="rounded-xl"
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div>
                <h1 className="text-3xl font-bold text-slate-900">Visualizar Proposta</h1>
                <p className="text-slate-600 mt-1">
                  Proposta {numeroPropostaDisplay}
                </p>
                <div className="mt-3 grid gap-2 text-sm text-slate-700 sm:grid-cols-3">
                  <div className="rounded-lg border border-slate-200 bg-white/80 px-3 py-2 shadow-sm">
                    <span className="block text-xs font-semibold uppercase tracking-wide text-slate-500">Instituição</span>
                    <span className="font-medium">{instituicaoDisplay}</span>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-white/80 px-3 py-2 shadow-sm">
                    <span className="block text-xs font-semibold uppercase tracking-wide text-slate-500">Data da proposta</span>
                    <span className="font-medium">{dataPropostaDisplay}</span>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-white/80 px-3 py-2 shadow-sm">
                    <span className="block text-xs font-semibold uppercase tracking-wide text-slate-500">Valor total da proposta</span>
                    <span className="font-semibold">{valorTotalDisplay}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-3 flex-wrap">
              {modoEdicao ? (
                <>
                  <Button
                    variant="outline"
                    onClick={() => setModoEdicao(false)}
                  >
                    <X className="w-4 h-4 mr-2" />
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleSalvarEdicao}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Salvar
                  </Button>
                </>
              ) : (
                <>
                  {podeUsarAssina ? (
                    <Button
                      variant="outline"
                      onClick={() => void handleAssina()}
                      disabled={geratingPDF || assinaBusy || enviandoEmail || versionando}
                      title="Cria envelope no Assina com este PDF; depois de assinar, baixe o PDF final na mesma tela"
                    >
                      <PenLine className="w-4 h-4 mr-2" />
                      {assinaBusy ? "Preparando…" : "Assina"}
                    </Button>
                  ) : null}
                  <Button
                    variant="outline"
                    onClick={() => void handleVersionarProposta()}
                    disabled={geratingPDF || assinaBusy || enviandoEmail || versionando}
                    title="Cria uma nova versão editável preservando a proposta atual"
                  >
                    <CopyPlus className="w-4 h-4 mr-2" />
                    {versionando ? "Criando..." : "Versionar"}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => void handleEnviarEmail()}
                    disabled={geratingPDF || assinaBusy || enviandoEmail || versionando}
                    title="Gera o PDF atual e envia por e-mail"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    {enviandoEmail ? "Enviando..." : "Enviar por e-mail"}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setModoEdicao(true)}
                    disabled={enviandoEmail || versionando}
                  >
                    <Edit2 className="w-4 h-4 mr-2" />
                    Editar Campos
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => navigate(createPageUrl("CriarProposta") + `?id=${proposta.id}`)}
                    disabled={enviandoEmail || versionando}
                  >
                    <Edit2 className="w-4 h-4 mr-2" />
                    Editar Completo
                  </Button>
                  <Button
                    onClick={handleDownloadPDF}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    disabled={geratingPDF || enviandoEmail || versionando}
                    title="PDF com texto real (~10 pt), pesquisável"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {geratingPDF ? 'Gerando...' : 'Baixar PDF'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handlePrint}
                    disabled={geratingPDF || enviandoEmail || versionando}
                    title="PDF com texto real (~10 pt), pesquisável no leitor"
                  >
                    <Printer className="w-4 h-4 mr-2" />
                    {geratingPDF ? 'Gerando...' : 'Imprimir'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => navigate(createPageUrl("Propostas"))}
                    title="Fechar e voltar à lista de propostas"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Fechar
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-br from-slate-50 to-blue-50 p-3 sm:p-4 md:p-6 pt-0">
        <div className="relative isolate mx-auto w-full min-w-0 max-w-7xl overflow-x-auto overflow-y-visible bg-white px-3 py-6 pb-14 shadow-xl print:max-w-[21cm] print:overflow-visible sm:px-5 sm:py-8 md:px-8 md:py-10">
          {config.marca_dagua_url && (
            <div
              className="pointer-events-none absolute right-4 top-24 z-0 max-w-[min(220px,42vw)] sm:right-6 sm:top-28"
              style={{
                opacity: (config.marca_dagua_transparencia || 10) / 100,
              }}
              aria-hidden
            >
              <AuthenticatedUploadImage
                src={config.marca_dagua_url}
                alt=""
                className="h-auto w-full object-contain opacity-100"
                style={{ maxHeight: "160px" }}
              />
            </div>
          )}

          {/* Cabeçalho - será capturado separadamente para o PDF */}
          {(config.cabecalho_url || config.cabecalho_texto) && (
            <div className="pdf-header-section relative z-[1] mb-6 text-center">
              {config.cabecalho_url ? (
                <AuthenticatedUploadImage
                  src={config.cabecalho_url}
                  alt="Cabeçalho"
                  style={{ maxWidth: '100%', height: 'auto', maxHeight: '25mm', objectFit: 'contain', margin: '0 auto' }}
                />
              ) : (
                <div className="inline-block rounded-md bg-blue-50 p-2 text-left dark:bg-slate-900">
                  <RichHtmlFragment html={config.cabecalho_texto} className="text-xs" />
                </div>
              )}
            </div>
          )}

          <div
            className="print-main-content proposta-document relative z-10 min-w-0 max-w-full text-slate-900 leading-[1.4] sm:leading-[1.45]"
            style={{ fontFamily: "Arial, Helvetica, sans-serif" }}
          >
            <section className={`proposta-bloco-dados ${propostaCardClass} ${propostaBlocoGapClass}`}>
              <div className={`mb-5 text-[10pt] ${prosePdfClass}`}>
                <p className="mb-1 font-bold text-slate-800">{instituicaoFmt.preposicao}</p>
                <p className="m-0 text-slate-900">
                  {displayField(removerReferenciasEdital(instituicaoFmt.nomeExibicao))}
                </p>
              </div>

              <dl className="grid grid-cols-1 gap-x-8 gap-y-3.5 text-[10pt] sm:grid-cols-[minmax(148px,34%)_1fr]">
                <dt className="font-semibold text-slate-700 [word-break:normal]">Agente de Contratação</dt>
                <dd className={`min-w-0 text-slate-900 ${prosePdfClass}`}>
                  {displayField(removerReferenciasEdital(proposta.agente_contratacao))}
                </dd>

                <dt className="font-semibold text-slate-700 [word-break:normal]">Referente</dt>
                <dd className={`min-w-0 text-slate-900 ${prosePdfClass}`}>
                  {displayField(formatReferentePregao(proposta))}
                </dd>

                <dt className="font-semibold text-slate-700 [word-break:normal]">Processo</dt>
                <dd className={`min-w-0 text-slate-900 ${prosePdfClass}`}>
                  {displayField(removerReferenciasEdital(proposta.processo_numero))}
                </dd>

                <dt className="font-semibold text-slate-700 [word-break:normal]">Data do certame</dt>
                <dd className="min-w-0 text-slate-900 [word-break:normal]">
                  {(() => {
                    const dataCertame = parseDataAbertura(edital?.data_abertura || edital?.dataAbertura);
                    return dataCertame
                      ? format(dataCertame, "EEEE, dd 'de' MMMM 'de' yyyy", { locale: ptBR })
                      : "Não informado";
                  })()}
                </dd>

                <dt className="font-semibold text-slate-700 [word-break:normal]">Hora</dt>
                <dd className="min-w-0 text-slate-900">
                  {displayField(edital?.hora_abertura || edital?.horaAbertura)}
                </dd>

                <dt className="font-semibold text-slate-700 [word-break:normal]">Sistema</dt>
                <dd className={`min-w-0 text-slate-900 ${prosePdfClass}`}>
                  {displayField(removerReferenciasEdital(proposta.sistema))}
                </dd>

                <dt className="font-semibold text-slate-700 [word-break:normal]">Identificação</dt>
                <dd className={`min-w-0 text-slate-900 ${prosePdfClass}`}>
                  {displayField(removerReferenciasEdital(proposta.identificacao))}
                </dd>
              </dl>
            </section>

            <div className={`proposta-titulo-principal ${propostaBlocoGapClass} rounded-lg border border-slate-300 bg-gradient-to-b from-slate-50 to-slate-100/80 py-3.5 text-center text-[11pt] font-bold uppercase tracking-wide text-slate-900 shadow-sm`}>
              Proposta de preços comercial
            </div>

            <section className={`proposta-bloco-texto ${propostaBlocoGapClass} text-[10pt] ${prosePdfClass}`}>
              <div className={`mx-auto ${propostaMaxReadableClass}`}>
                <p className="mb-2 font-semibold text-slate-900">Prezado(a) Senhor(a),</p>
                <p className="mb-3 text-slate-900">
                  A empresa {displayField(config.razao_social)}, inscrita no CNPJ nº {displayField(config.cnpj)}, apresenta
                  formalmente esta proposta de preços para a aquisição dos bens e/ou serviços indicados, nos termos do Termo
                  de Referência e do edital em epígrafe.
                </p>
                <p className="mb-3 text-slate-800 [overflow-wrap:anywhere] [word-break:normal]">
                  <span className="font-medium text-slate-600">Sede / endereço</span>
                  <br />
                  {displayField(config.endereco_completo)}
                </p>
                <p className="mb-3 text-slate-800">
                  {displayField(config.municipio)} / {displayField(config.uf)} — CEP {displayField(config.cep)}.
                  <br />
                  <span className="text-slate-600">Telefone: </span>
                  {displayField(config.telefone_1)}
                </p>
                <p className="m-0 text-slate-900">
                  O(A) representante legal {displayField(config.socio_nome)} subscreve esta proposta e declara ciência das
                  condições do certame.
                </p>
              </div>
            </section>

            <section className={`proposta-bloco-objeto ${propostaCardClass} ${propostaBlocoGapClass} text-[10pt]`}>
              <h3 className={propostaTituloSecaoClass}>Objeto</h3>
              <p className={`mt-0 min-h-0 whitespace-pre-wrap ${prosePdfClass}`}>
                {displayField(removerReferenciasEdital(proposta.objeto), "Não informado")}
              </p>
            </section>

            {/* Tabela de Itens — rolagem horizontal no mobile; PDF fatia por altura; blocos “evitar corte” só em seções curtas */}
            <div className="w-full min-w-0">
              {(() => {
                // Verifica se há itens para exibir
                if (!proposta.itens || proposta.itens.length === 0) {
                  return (
                    <div className="mb-4 p-4 border border-slate-300 rounded text-center text-slate-500 text-sm">
                      Nenhum item na proposta
                    </div>
                  );
                }

                // Determina as colunas a usar
                let colunasConfig = [];

                if (proposta.colunas_itens && proposta.colunas_itens.length > 0) {
                  // Usa colunas configuradas (importadas de planilha)
                  colunasConfig = proposta.colunas_itens.filter(c => c.visivel !== false);
                } else {
                  // Colunas padrão
                  colunasConfig = [
                    { id: "item", nome: "Item", tipo: "numero" },
                    { id: "descricao", nome: "Descrição", tipo: "texto_longo" },
                    { id: "unidade", nome: "UND", tipo: "texto" },
                    { id: "quantidade", nome: "Qtd", tipo: "numero" },
                    { id: "valor_unitario", nome: "Valor Unitário", tipo: "valor" },
                    { id: "valor_total", nome: "Total", tipo: "valor" }
                  ];
                }

                const nCol = colunasConfig.length;
                const largurasColunasPct = getPropostaColunasWidthsPercent(colunasConfig);

                const formatarNumLocal = (n) =>
                  new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 6 }).format(n);

                const formatarCelula = (item, coluna) => {
                  if (coluna.tipo === "calculada") {
                    const vals = aplicarColunasCalculadas(item, colunasConfig);
                    const v = vals[coluna.id];
                    if (v === null || v === undefined || v === "") return "-";
                    return formatCurrency(v);
                  }
                  const valor = item[coluna.id];
                  if (valor === null || valor === undefined || valor === "") return "-";
                  if (coluna.tipo === "valor") return formatCurrency(valor);
                  if (coluna.tipo === "numero") return valor;
                  return valor;
                };

                const cellBase =
                  nCol >= 8
                    ? "border border-slate-400 px-1.5 py-2 align-top text-[8pt] leading-[1.35] sm:px-2 sm:py-2.5 sm:text-[8.5pt] sm:leading-[1.4]"
                    : "border border-slate-400 px-3 py-3 align-top text-[9pt] leading-[1.4] sm:px-3.5 sm:py-3.5 sm:text-[10pt] sm:leading-[1.45]";
                /** Importante: não usar word-break:normal na base — competia com a descrição e cortava texto na tela. */
                const cellTextoLongo =
                  "text-left !leading-[1.55] sm:!leading-[1.6] whitespace-pre-wrap !break-words overflow-visible [overflow-wrap:anywhere] [word-break:break-word]";
                const cellTextoCurto =
                  "whitespace-normal break-words [overflow-wrap:anywhere] [word-break:break-word] leading-[1.45]";
                const cellValor =
                  nCol >= 9
                    ? "proposta-cell-valor min-w-0 whitespace-nowrap text-right align-top tabular-nums font-semibold text-slate-900 text-[7.25pt] sm:text-[8pt] [word-break:keep-all]"
                    : nCol >= 8
                      ? "proposta-cell-valor min-w-0 whitespace-nowrap text-right align-top tabular-nums font-semibold text-slate-900 text-[7.75pt] sm:text-[8.75pt] [word-break:keep-all]"
                      : "proposta-cell-valor min-w-0 whitespace-nowrap text-right align-top tabular-nums font-semibold text-slate-900 [word-break:keep-all]";
                const cellNumero =
                  "whitespace-nowrap text-center align-top tabular-nums text-slate-900";
                const thBase =
                  nCol >= 8
                    ? "border border-slate-500 bg-slate-200 px-1.5 py-2 align-top text-[7.5pt] font-bold uppercase leading-tight tracking-wide text-slate-900 sm:px-2 sm:py-2.5 sm:text-[8pt]"
                    : "border border-slate-500 bg-slate-200 px-3 py-3 align-top text-[9pt] font-bold uppercase leading-normal tracking-wide text-slate-900 sm:text-[9.5pt]";

                const renderLinhaCabecalhoColunas = (keySuffix) => (
                  <tr key={`hdr-${keySuffix}`} className="proposta-linha-cabecalho-colunas">
                    {colunasConfig.map((col) => (
                      <th
                        key={col.id}
                        scope="col"
                        className={`${thBase} ${getPropostaColunaTabelaClass(col, nCol)} ${getPropostaThHeaderClass(col)} ${
                          col.tipo === "valor" || col.tipo === "calculada"
                            ? "text-right"
                            : col.tipo === "numero"
                              ? "text-center"
                              : "text-left"
                        }`}
                      >
                        {col.nome}
                      </th>
                    ))}
                  </tr>
                );

                const itensLista = proposta.itens || [];
                const precisaCabecalhoInicial =
                  itensLista.length > 0 &&
                  getTipoLinha(itensLista[0]) !== TIPO_LINHA.CABECALHO_GRUPO;

                return (
                  <section className={`proposta-bloco-tabela ${propostaBlocoGapClass} w-full min-w-0 max-w-full`}>
                    <h3 className={`${propostaTituloSecaoClass} border-slate-300`}>Composição de preços</h3>
                    <div className="proposta-tabela-wrapper box-border w-full min-w-0 max-w-full overflow-x-auto overflow-y-visible overscroll-x-contain rounded-xl border border-slate-300 bg-white shadow-md">
                      <table
                        className={`proposta-pdf-table w-full table-fixed border-collapse [&_th]:!overflow-visible [&_td]:!overflow-visible ${nCol >= 8 ? "text-[8pt] sm:text-[8.5pt]" : "text-[9pt]"}`}
                        style={{ minWidth: getPropostaTabelaItensMinWidthPx(nCol) }}
                      >
                        <colgroup>
                          {colunasConfig.map((col, idx) => (
                            <col
                              key={col.id ?? `col-${idx}`}
                              style={getPropostaColElementStyle(col, largurasColunasPct[idx])}
                            />
                          ))}
                        </colgroup>
                        <tbody>
                          {precisaCabecalhoInicial ? renderLinhaCabecalhoColunas("inicial") : null}
                          {itensLista.map((item, index) => {
                            const tipoLinha = getTipoLinha(item);
                            if (tipoLinha === TIPO_LINHA.CABECALHO_GRUPO) {
                              return (
                                <React.Fragment key={`grupo-${index}`}>
                                  <tr key={`grupo-titulo-${index}`} className="bg-amber-50">
                                    <td
                                      colSpan={colunasConfig.length}
                                      className={`${cellBase} bg-amber-100/90 font-bold text-amber-950`}
                                    >
                                      {item.titulo_grupo || "Grupo"}
                                    </td>
                                  </tr>
                                  {renderLinhaCabecalhoColunas(`pos-${index}`)}
                                </React.Fragment>
                              );
                            }
                            if (tipoLinha === TIPO_LINHA.SUBTOTAL) {
                              const labelSpan = getSubtotalLabelColSpan(colunasConfig);
                              return (
                                <tr
                                  key={index}
                                  className="proposta-linha-subtotal border-t-2 border-slate-400 bg-slate-200/95 font-bold text-slate-900"
                                >
                                  <td
                                    colSpan={labelSpan}
                                    className={`${cellBase} proposta-celula-subtotal-label text-left align-middle text-slate-900 [overflow-wrap:normal] [word-break:normal]`}
                                  >
                                    Subtotal do grupo
                                  </td>
                                  {colunasConfig.slice(labelSpan).map((col) => {
                                    const soma = somarColunaAteSubtotal(
                                      proposta.itens,
                                      index,
                                      col,
                                      colunasConfig
                                    );
                                    return (
                                      <td
                                        key={col.id}
                                        className={`${cellBase} ${getPropostaColunaTabelaClass(col, nCol)} ${
                                          col.tipo === "valor" || col.tipo === "calculada"
                                            ? cellValor
                                            : cellNumero
                                        }`}
                                      >
                                        {col.tipo === "numero"
                                          ? formatarNumLocal(soma)
                                          : formatCurrency(soma)}
                                      </td>
                                    );
                                  })}
                                </tr>
                              );
                            }
                            return (
                              <tr key={index} className="odd:bg-white even:bg-slate-50/70">
                                {colunasConfig.map((col) => (
                                  <td
                                    key={col.id}
                                    className={`${cellBase} ${getPropostaColunaTabelaClass(col, nCol)} ${
                                      col.tipo === "valor" || col.tipo === "calculada"
                                        ? cellValor
                                        : isPropostaColunaDescricao(col)
                                          ? cellTextoLongo
                                          : col.tipo === "numero"
                                            ? cellNumero
                                            : col.tipo === "texto"
                                              ? `text-center ${cellTextoCurto}`
                                              : "proposta-celula-texto-curto text-center align-top whitespace-normal [overflow-wrap:anywhere] [word-break:break-word]"
                                    }`}
                                  >
                                    {formatarCelula(item, col)}
                                  </td>
                                ))}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </section>
                );
              })()}
            </div>

            {/* Valor Total da Proposta */}
            {proposta.valor_total_proposta > 0 && (
              <div className="valor-total-box page-break-avoid mb-8 overflow-visible rounded-xl border-2 border-slate-800 bg-gradient-to-b from-white to-slate-50 p-4 shadow-md break-inside-avoid sm:p-5">
                <div className="flex flex-col gap-1.5 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
                  <span className="shrink-0 text-[10pt] font-semibold uppercase tracking-wide text-slate-600">
                    Valor total da proposta
                  </span>
                  <span className="proposta-valor-total-numero shrink-0 text-right text-[14pt] font-bold tabular-nums text-slate-900 sm:text-[15pt]">
                    {formatCurrency(proposta.valor_total_proposta)}
                  </span>
                </div>
                <div
                  className={`campo-extenso-total mt-3 min-h-0 overflow-visible border-t border-slate-200 pt-3 text-[9.5pt] italic leading-[1.45] text-slate-500 ${prosePdfClass}`}
                >
                  ({displayField(proposta.texto_valor_total, "Valor global da proposta de preços")})
                </div>
              </div>
            )}

            {/* Campos Extras (Descrição + Valor + Extenso) */}
            {proposta.campos_extras && proposta.campos_extras.length > 0 && (
              <div className={`campos-extras-section ${propostaBlocoGapClass} space-y-4`}>
                <h3 className={propostaTituloSecaoClass}>Demais valores</h3>
                {proposta.campos_extras.map((campo, index) => (
                  <div
                    key={campo.id || index}
                    className="campo-extra-item page-break-avoid min-h-0 overflow-visible rounded-xl border border-slate-300 bg-white p-5 shadow-sm break-inside-avoid"
                  >
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-baseline sm:justify-between">
                      <span className="min-w-0 flex-1 text-[10pt] font-medium leading-[1.45] text-slate-800 [word-break:normal] [overflow-wrap:anywhere]">
                        {displayField(campo.descricao)}
                      </span>
                      <span className="proposta-valor-total-numero shrink-0 text-right text-[12pt] font-bold tabular-nums text-slate-900 sm:min-w-[8rem] sm:pl-4">
                        {formatCurrency(campo.valor)}
                      </span>
                    </div>
                    {campo.extenso && (
                      <div
                        className={`campo-extenso-total mt-4 min-h-0 overflow-visible border-t border-slate-100 pt-3 text-[9.5pt] italic leading-[1.45] text-slate-500 ${prosePdfClass}`}
                      >
                        {campo.extenso}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {proposta.observacoes && (
              <section className={`proposta-bloco-observacoes ${propostaCardClass} ${propostaBlocoGapClass} text-[10pt] ${prosePdfClass}`}>
                <h3 className={propostaTituloSecaoClass}>Observações</h3>
                <p className="mt-0 min-h-0 whitespace-pre-wrap break-words [overflow-wrap:break-word] [word-break:normal]">
                  {proposta.observacoes}
                </p>
              </section>
            )}

            <section className={`proposta-bloco-prazos ${propostaCardClass} ${propostaBlocoGapClass} text-[10pt]`}>
              <h3 className={propostaTituloSecaoClass}>Prazos e outros</h3>
              <ul className="m-0 list-none space-y-4 p-0">
                <li className="grid gap-1.5 sm:grid-cols-[minmax(10rem,34%)_1fr] sm:items-start sm:gap-x-5">
                  <span className="shrink-0 font-medium text-slate-600">Validade da proposta</span>
                  <span className="min-w-0 text-slate-900 [overflow-wrap:break-word] [word-break:normal]">
                    {displayField(proposta.prazoValidade ?? proposta.prazo_validade)}
                  </span>
                </li>
                <li className="grid gap-1.5 sm:grid-cols-[minmax(10rem,34%)_1fr] sm:items-start sm:gap-x-5">
                  <span className="shrink-0 font-medium text-slate-600">Entrega</span>
                  <span className="min-w-0 text-slate-900 [overflow-wrap:break-word] [word-break:normal]">
                    {displayField(proposta.prazoEntrega ?? proposta.prazo_entrega)}
                  </span>
                </li>
                <li className="grid gap-1.5 sm:grid-cols-[minmax(10rem,34%)_1fr] sm:items-start sm:gap-x-5">
                  <span className="shrink-0 font-medium text-slate-600">Vigência do contrato</span>
                  <span className="min-w-0 text-slate-900 [overflow-wrap:break-word] [word-break:normal]">
                    {displayField(proposta.prazoVigencia ?? proposta.prazo_vigencia)}
                  </span>
                </li>
                <li className="grid gap-1.5 sm:grid-cols-[minmax(10rem,34%)_1fr] sm:items-start sm:gap-x-5">
                  <span className="shrink-0 font-medium text-slate-600">Pagamento</span>
                  <span className="min-w-0 text-slate-900 [overflow-wrap:break-word] [word-break:normal]">
                    {displayField(proposta.prazoPagamento ?? proposta.prazo_pagamento)}
                  </span>
                </li>
                {(proposta.prazos_extras || []).map((row, idx) => {
                  const titulo = row?.titulo != null ? String(row.titulo).trim() : "";
                  const conteudo = row?.conteudo != null ? String(row.conteudo).trim() : "";
                  if (!titulo && !conteudo) return null;
                  return (
                    <li
                      key={row.id || `prazo-extra-${idx}`}
                      className="grid gap-1.5 sm:grid-cols-[minmax(10rem,34%)_1fr] sm:items-start sm:gap-x-5"
                    >
                      <span className="shrink-0 font-medium text-slate-600">
                        {displayField(titulo || "—")}
                      </span>
                      <span className="min-w-0 text-slate-900 whitespace-pre-wrap [overflow-wrap:break-word] [word-break:normal]">
                        {displayField(conteudo || "—")}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </section>

            <section
              className={`proposta-bloco-declaracoes page-break-avoid break-inside-avoid ${propostaCardClass} ${propostaBlocoGapClass} text-[10pt] ${prosePdfClass}`}
            >
              <h3 className={propostaTituloSecaoClass}>Declarações</h3>
              <p className="mb-0 text-slate-800">
                Informamos que nos preços apresentados estão computados todos os custos necessários à prestação dos
                serviços ou ao fornecimento, incluindo impostos, encargos trabalhistas e previdenciários, demais encargos
                fiscais e comerciais, taxas, seguros, deslocamentos e demais que incidam direta ou indiretamente.
              </p>
            </section>

            <section
              className={`proposta-bloco-banco page-break-avoid break-inside-avoid ${propostaCardClass} ${propostaBlocoGapClass} bg-slate-50/95 text-[10pt]`}
            >
              <h3 className={`${propostaTituloSecaoClass} border-slate-300`}>Dados bancários</h3>
              <dl className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div>
                  <dt className="font-semibold text-slate-700">Conta corrente</dt>
                  <dd className="mt-1 tabular-nums text-slate-900">{displayField(config.banco_conta)}</dd>
                </div>
                <div>
                  <dt className="font-semibold text-slate-700">Código do banco</dt>
                  <dd className="mt-1 tabular-nums text-slate-900">{displayField(config.banco_codigo)}</dd>
                </div>
                <div>
                  <dt className="font-semibold text-slate-700">Agência / instituição</dt>
                  <dd className="mt-1 text-slate-900">
                    {displayField(
                      [config.banco_agencia, config.banco_nome].filter(Boolean).join(" — ") || null
                    )}
                  </dd>
                </div>
              </dl>
            </section>

            <section
              className={`proposta-bloco-empresa page-break-avoid break-inside-avoid ${propostaCardClass} ${propostaBlocoGapClass} bg-slate-50/90 text-[10pt]`}
            >
              <h3 className={`${propostaTituloSecaoClass} border-slate-300`}>
                Dados para contrato e ata de registro de preços
              </h3>

              <dl className="mb-5 grid grid-cols-1 gap-y-2 rounded-lg border border-slate-200 bg-white/90 p-4 shadow-sm sm:grid-cols-[160px_1fr]">
                <dt className="font-semibold text-slate-700">Razão social</dt>
                <dd className="min-w-0 text-slate-900 [word-break:normal]">{displayField(config.razao_social)}</dd>
                <dt className="font-semibold text-slate-700">CNPJ</dt>
                <dd className="min-w-0 font-medium tabular-nums text-slate-900">{displayField(config.cnpj)}</dd>
                <dt className="font-semibold text-slate-700">Endereço</dt>
                <dd className={`min-w-0 text-slate-900 ${prosePdfClass}`}>{displayField(config.endereco_completo)}</dd>
                <dt className="font-semibold text-slate-700">Município / UF / CEP</dt>
                <dd className="min-w-0 text-slate-900 [word-break:normal]">
                  {displayField(config.municipio)} / {displayField(config.uf)} / {displayField(config.cep)}
                </dd>
                <dt className="font-semibold text-slate-700">E-mail</dt>
                <dd className="min-w-0 text-slate-900 [word-break:anywhere]">{displayField(config.email)}</dd>
                <dt className="font-semibold text-slate-700">Telefone(s)</dt>
                <dd className="min-w-0 text-slate-900">
                  {displayField(
                    [config.telefone_1, config.telefone_2].filter(Boolean).join(" / ") || null
                  )}
                </dd>
                <dt className="font-semibold text-slate-700">Representante legal</dt>
                <dd className="min-w-0 text-slate-900">{displayField(config.socio_nome)}</dd>
                <dt className="font-semibold text-slate-700">Inscrições</dt>
                <dd className="min-w-0 text-slate-900 [word-break:normal]">
                  Municipal {displayField(config.inscricao_municipal)} — Estadual{" "}
                  {displayField(config.inscricao_estadual)}
                </dd>
              </dl>

              <div className="rounded-lg border border-slate-200 bg-white/90 p-4 shadow-sm">
                <h4 className="mb-3 text-[10pt] font-semibold uppercase tracking-wide text-slate-700">
                  Representante para assinatura
                </h4>
                <dl className="grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-[140px_1fr]">
                  <dt className="font-semibold text-slate-700">Nome</dt>
                  <dd>{displayField(config.socio_nome)}</dd>
                  <dt className="font-semibold text-slate-700">Cargo</dt>
                  <dd>{displayField(config.socio_cargo)}</dd>
                  <dt className="font-semibold text-slate-700">Nacionalidade</dt>
                  <dd>{displayField(config.socio_nacionalidade)}</dd>
                  <dt className="font-semibold text-slate-700">Profissão</dt>
                  <dd>{displayField(config.socio_profissao)}</dd>
                </dl>
              </div>
            </section>

            <div className="page-break-avoid break-inside-avoid">
              <div className={`mb-6 text-[10pt] ${prosePdfClass}`}>
                <p className="mb-0 text-slate-800">
                  Declaramos estar de pleno acordo com as condições do edital e documentos convocados.
                </p>
              </div>

              <div className="mb-5 text-[10pt] leading-[1.45]">
                {modoEdicao ? (
                  <div className="flex items-center gap-2 hide-on-print">
                    <Input
                      value={localProposta}
                      onChange={(e) => setLocalProposta(e.target.value)}
                      className="w-40"
                      placeholder="Cidade/UF"
                    />
                    ,
                    <Input
                      type="date"
                      value={dataProposta}
                      onChange={(e) => setDataProposta(e.target.value)}
                      className="w-48"
                    />
                  </div>
                ) : (
                  <p>{localProposta}, {formatDate(dataProposta)}</p>
                )}
              </div>

              <BlocoAssinaturaEmpresa config={config} />
            </div>
          </div>
          
          {/* Rodapé - será capturado separadamente para o PDF */}
          {(config.rodape_url || config.rodape_texto) && (
            <div className="pdf-footer-section relative z-10 mt-14 clear-both w-full min-w-0 border-t border-slate-100 pt-10 pb-6">
              {config.rodape_url ? (
                <div className="flex w-full justify-center">
                  <AuthenticatedUploadImage
                    src={config.rodape_url}
                    alt="Rodapé"
                    style={{ maxWidth: '100%', height: 'auto', maxHeight: '25mm', objectFit: 'contain' }}
                  />
                </div>
              ) : (
                <div className="w-full max-w-full min-w-0 rounded-lg bg-blue-50 p-4 text-left dark:bg-slate-900">
                  <RichHtmlFragment html={config.rodape_texto} className="text-xs" />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}